import { getSystemLocale } from "@/lib/locale/locale";
import { hasUnavailbleDayInBetween } from "@/offering_v2/components/date_range_picker/date_range_picker_utils";
import {
  addDays,
  getDate,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isEqual,
  isSameMonth,
  parseISO,
} from "date-fns";
import { isEmpty } from "lodash";
import { FBT, Locale } from "../lib/i18n/i18n_provider";
import { DateInterval, isSameDay as isSameDate } from "./date_utils";
import { parseDay } from "./day_utils";
import { DayInterval } from "./day_utils";
import { MeetingRoomDateRange } from "./order_params";
import { parseTime } from "./time_utils";
import { AvailableDay } from "./api";

export function formatDateRange(
  value: DateInterval,
  fbt: FBT,
  locale: Locale,
  fallback?: string
): string {
  if (!value) {
    return fallback || "When";
  }

  if (value.start && value.end) {
    const { start, end } = value;

    if (isSameDate(start, end)) {
      return fbt.formatDate(start);
    }

    return `${fbt.formatDate(start)} ${
      locale.direction === "ltr" ? "→" : "←"
    } ${fbt.formatDate(end)}`;
  }

  return fallback || "When";
}

export function getNonConsecutiveDateRanges(
  startDate: Date,
  endDate: Date,
  availableDates: Date[]
): { startDate: Date; endDate: Date }[] {
  if (isSameDate(startDate, endDate)) {
    return [{ startDate, endDate }];
  }

  const range = availableDates.slice(
    availableDates.findIndex((date) => isSameDate(date, startDate)),
    availableDates.findIndex((date) => isSameDate(date, endDate)) + 1
  );
  const ranges: { startDate: Date; endDate: Date }[] = [];
  let startDateInRange = range[0];
  let endDateInRange = range[0];
  for (let i = 0; i < range.length; i++) {
    const date = range[i + 1];
    const nextDate = addDays(endDateInRange, 1);
    if (isSameDate(date, nextDate)) {
      endDateInRange = date;
      if (isSameDate(endDate, date)) {
        ranges.push({ startDate: startDateInRange, endDate: date });
        break;
      }
    } else {
      ranges.push({ startDate: startDateInRange, endDate: endDateInRange });
      startDateInRange = date;
      endDateInRange = date;
    }
  }

  return ranges;
}

export function toNonConsecutiveDateRangeString(
  ranges: {
    startDate: Date;
    endDate: Date;
  }[],
  monthTextLength: "short" | "long" = "short",
  locale: string = "en-US"
): string {
  let month: number;
  const rangeStrings = ranges.map((range, index) => {
    let dateRangeText: string;
    if (isSameMonth(range.startDate, range.endDate)) {
      const currentMonth = getMonth(range.startDate);
      const onlyDateRange = isSameDate(range.startDate, range.endDate)
        ? `${getDate(range.startDate)}`
        : `${getDate(range.startDate)}-${getDate(range.endDate)}`;
      if (month === currentMonth) {
        dateRangeText = onlyDateRange;
      } else {
        month = currentMonth;
        dateRangeText = `${range.startDate.toLocaleDateString(locale, {
          month: monthTextLength,
        })} ${onlyDateRange}`;
      }
    } else {
      dateRangeText = `${range.startDate.toLocaleDateString(locale, {
        month: monthTextLength,
      })} ${getDate(range.startDate)} - ${range.endDate.toLocaleDateString(
        locale,
        {
          month: monthTextLength,
        }
      )} ${getDate(range.endDate)}`;
    }

    if (index === ranges.length - 1) {
      dateRangeText += `, ${getYear(range.endDate)}`;
    }

    return dateRangeText;
  });

  if (isEmpty(rangeStrings)) {
    return "";
  }

  if (rangeStrings.length === 1) {
    return rangeStrings[0];
  }

  const concated =
    rangeStrings.slice(0, rangeStrings.length - 1).join(", ") +
    ` & ${rangeStrings[rangeStrings.length - 1]}`;

  return concated;
}

export function formatDayRange(
  value: DayInterval,
  fbt: FBT,
  locale: Locale,
  fallback?: string,
  availableDates?: Date[]
): string {
  const start = parseDay(value.start);
  const end = parseDay(value.end);
  if (availableDates && hasUnavailbleDayInBetween(start, end, availableDates)) {
    const dateRanges = getNonConsecutiveDateRanges(start, end, availableDates);
    let dateRangesString = toNonConsecutiveDateRangeString(
      dateRanges,
      "long",
      getSystemLocale()
    );
    const lengthThreshold = 30;
    if (dateRangesString.length > lengthThreshold) {
      dateRangesString = toNonConsecutiveDateRangeString(
        dateRanges,
        "short",
        getSystemLocale()
      );
    }
    return dateRangesString;
  }
  return formatDateRange(
    {
      start,
      end,
    },
    fbt,
    locale,
    fallback
  );
}

export function formatDay(value: Date, fbt: FBT): string {
  return fbt.formatDate(value);
}

export function formatMeetingRoomDateRange(
  value: MeetingRoomDateRange | null,
  fbt: FBT,
  locale: Locale,
  fallback: string = "When"
): string {
  if (!value) {
    return fallback;
  }

  if (value.date) {
    if (value.startTime && value.endTime) {
      const day = parseDay(value.date);
      const startTime = parseTime(value.startTime);
      const endTime = parseTime(value.endTime);

      return `${fbt.formatDate(day)}, ${fbt.formatTime(startTime)} ${
        locale.direction === "ltr" ? "→" : "←"
      } ${fbt.formatTime(endTime)}`;
    } else if (value.startTime) {
      const day = parseDay(value.date);
      const startTime = parseTime(value.startTime);

      return `${fbt.formatDate(day)}, ${fbt.formatTime(startTime)}`;
    } else {
      const day = parseDay(value.date);
      return `${fbt.formatDate(day)}`;
    }
  }

  return fallback;
}

export function formatMeetingRoomTimeRange(
  value: MeetingRoomDateRange | null,
  fbt: FBT,
  locale: Locale,
  fallback: string = "When"
): string {
  if (!value) {
    return fallback;
  }

  if (value.date && value.startTime && value.endTime) {
    const startTime = parseTime(value.startTime);
    const endTime = parseTime(value.endTime);

    return `${fbt.formatTime(startTime)} ${
      locale.direction === "ltr" ? "→" : "←"
    } ${fbt.formatTime(endTime)}`;
  }

  return fallback;
}

export function getNonConsecutiveDates(
  startDateStr: string,
  endDateStr: string,
  availableDays: AvailableDay[]
): string[] {
  const startDate = parseISO(startDateStr);
  const endDate = parseISO(endDateStr);

  const filtereDates = availableDays
    .filter((d) => {
      const date = parseISO(d.date);

      return (
        d.availableFullDay &&
        startDate &&
        endDate &&
        ((isAfter(date, startDate) && isBefore(date, endDate)) ||
          isEqual(date, startDate) ||
          isEqual(date, endDate))
      );
    })
    .map((d) => d.date);

  return filtereDates;
}
